<template>
  <div class="p-5 flex flex-col">
    <p class="text-primary md:text-black font-semibold text-lg md:text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-multiple
              v-model="filters.flf_fc_nominate"
              placeholder="Select FLF/FC Nominate"
              :options="flfFcOptions"
              style="min-width: 260px;"
              :disabled="isFlfFc"
            />
          </div>
          <div class="flex items-center w-full">
            <select-with-multiple
              :hide-search="true"
              v-model="filters.vessel_name"
              placeholder="Select Vessel"
              :options="vesselOptions"
              style="min-width: 260px;"
            />
          </div>
          <div class="flex items-center w-full">
            <date-picker
              v-model="filters.month_year"
              type="month"
              format="MMM YYYY"
              value-type="MMM-YYYY"
              placeholder="Select Month"
              style="min-width: 260px;"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="flex flex-col md:bg-white md:rounded-lg h-full">
      <table-top
        is-search-has-border
        :hide-page-limit-selection="activeTab === 'summary_cargo_stolen'"
        :page-size-start="false"
        class="mt-6 mx-6"
        @fetch="fetchCargoStolen"
        ref="tabletop"
      >
        <template #start>
          <vs-button
            ref="buttonExport"
            class="button w-min font-semibold"
            color="secondary"
            @click="isDownload = true"
          >
            Export
          </vs-button>
          <vs-button
            v-show="true"
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="sendCargoStolenEmail"
          >
            Send Email
          </vs-button>
        </template>
      </table-top>
      <div class="flex border-b mt-5 ml-5">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="activeTab = tab.value"
          :class="['px-5 py-3 text-lg font-semibold text-white border-0 rounded-t-lg', activeTab === tab.value ? 'bg-primary border-0' : 'bg-gray-200 text-gray-500']"
        >
          {{ tab.label }}
        </button>
      </div>
      <div 
        v-if="activeTab === 'summary_cargo_stolen'"
        class="my-6 py-5 md:bg-white md:rounded-lg flex flex-col gap-8"
      >
        <PersentageChart 
          :chart-data="percentageCargoStolenChart" 
          :month-year="filters.month_year"
        />
        <SummaryCargoStolen
          :chart-data="summaryCargoStolen"
          :month-year="filters.month_year"
        />
      </div>
      <data-table
        v-else
        class="mt-6 md:bg-white md:rounded-lg"
        :table-header="computedTableHeaders"
        :table-data="tableData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :loading="isBusy"
        body-height="100vh"
        @sort="sort"
      >
        <template #no="{ index }">
          {{ (currentPage - 1) * pageSize + index + 1 }}
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-2">
            <div
              v-if="isUserCanAccessMenu(permission.name, 'add')"
              :disabled="row.status !== 'COMPLETED'"
              class="flex items-center justify-center icon-transparent-bg"
              @click="editCargoPopupShown(row)"
            >
              <img :src="require('@/assets/shipo-icon-edit.svg')">
            </div>
            <div
              v-if="activeTab === 'reported_accident'"
              class="flex items-center justify-center icon-transparent-bg"
              @click="viewAttachments(row)"
            >
              <img
                :src="require('@/assets/shipo-icon-see.svg') "
                class="w-8 h-8"
              >
            </div>
          </div>
        </template>
        <template #company="{ row }">
          <span>
            {{ row.company }}
          </span>
        </template>
        <template #trip_number="{ row }">
          <span>
            {{ row.trip_number }}
          </span>
        </template>
        <template #route="{ row }">
          <span>
            {{ row.route }}
          </span>
        </template>
        <template #tug_name="{ row }">
          <span>
            {{ row.tug_name }}
          </span>
        </template>
        <template #barge_name="{ row }">
          <span>
            {{ row.barge_name }}
          </span>
        </template>
        <template #cargo_loaded="{ row }">
          <span>
            {{ row.cargo_loaded ? row.cargo_loaded : '-' }}
          </span>
        </template>
        <template #supply_to_vessel="{ row }">
          <span>
            {{ row.supply_to_vessel }}
          </span>
        </template>
        <template
          #date_of_incident="{ row }"
        >
          <span>
            {{ row.date_of_incident ? moment(row.date_of_incident).format('DD MMM YYYY hh:mm') : '' }}
          </span>
        </template>
        <template
          #location_of_incident="{ row }"
        >
          <span>
            {{ row.location_of_incident }}
          </span>
        </template>
        <template
          #estimated_lost_cargo="{ row }"
        >
          <span>
            {{ row.estimated_lost_cargo }}
          </span>
        </template>
        <template
          #remarks="{ row }"
        >
          <span>
            {{ row.remarks }}
          </span>
        </template>
        <template
          #last_updated="{ row }"
        >
          <span>
            {{ row.last_updated ? moment(row.last_updated).format('DD MMM YYYY hh:mm') : '' }}
          </span>
        </template>
        <template
          #last_updated_by="{ row }"
        >
          <span>
            {{ row.last_updated_by }}
          </span>
        </template>
      </data-table>
      <table-bottom
        v-if="activeTab !== 'summary_cargo_stolen'"
        class="mt-6 mx-6"
        @fetch="fetchCargoStolen"
      />
    </div>
    <EditForm
      v-show="isEditCargoPopupShown"
      :active="isEditCargoPopupShown"
      :form="form"
      :active-tab="activeTab"
      @update:active="isEditCargoPopupShown = $event"
      @close="isEditCargoPopupShown = false"
      @updated="handleUpdated"
    />
    <EvidenceViewer
      :active="isEvidenceViewerShown"
      :evidences="evidenceList"
      @update:active="isEvidenceViewerShown = $event"
      @close="isEvidenceViewerShown = false"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithMultiple from '@/components/selectWithMultiple'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

import EditForm from './components/editForm.vue'
import EvidenceViewer from './components/evidenceViewer.vue'
import PersentageChart from './components/PersentageChart.vue'
import SummaryCargoStolen from './components/SummaryCargoStolen.vue'

export default {
  metaInfo: {
    title: `${PAGE_TITLE}`
  },
  components: {
    SelectWithMultiple,
    DatePicker,
    DataTable,
    TableTop,
    TableBottom,
    EditForm,
    EvidenceViewer,
    PersentageChart,
    SummaryCargoStolen
  },
  computed: {
    ...mapGetters({
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      totalPage: 'Table/totalPage',
      cargoStolen: 'CargoStolen/cargoStolen',
      vesselCargoStolen: 'CargoStolen/vesselCargoStolen',
      cargoStolenFiles: 'CargoStolen/cargoStolenFiles',
      flfFcNominates: 'VesselLineUp/flfFcNominates',
      me: 'Auth/me',
      percentageCargoStolenChart: 'CargoStolen/percentageCargoStolenChart',
      summaryCargoStolen: 'CargoStolen/summaryCargoStolen'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    computedTableHeaders() {
      const headers = [
        { label: 'No', key: 'no', width: '20px' },
        { label: 'Action', key: 'action', width: '50px' },
        { label: 'Company', key: 'company', sortable: true },
        { label: 'Trip Number', key: 'trip_number', sortable: true },
        { label: 'Route', key: 'route', sortable: true },
        { label: 'Tug Name', key: 'tug', sortable: true },
        { label: 'Barge Name', key: 'barge', sortable: true },
        { label: 'Cargo Loaded', key: 'cargo_loaded', sortable: true },
        { label: 'Supply To Vessel', key: 'name_of_vessel', sortable: true }
      ];
      if (this.activeTab === 'reported_accident') {
        headers.push(
          { label: 'Date Of Incident', key: 'date_of_incident', sortable: true },
          { label: 'Location Of Incident', key: 'location_of_incident', sortable: true },
          { label: 'Estimated Lost by Visual Cargo', key: 'estimated_lost_cargo', sortable: true },
          { label: 'Remark', key: 'remarks', sortable: true },
          { label: 'Report Submitted Date', key: 'last_updated', sortable: true },
          { label: 'Report Submitted By', key: 'last_updated_by', sortable: true }
        );
      }
      return headers;
    },
    isFlfFc() {
      return this.me && this.me.flfFcNominate && !this.isEmpty(this.me.flfFcNominate)
    }
  },
  data() {
    return {
      isDownload: false,
      isBusy: false,
      isEditCargoPopupShown: false,
      isEvidenceViewerShown: false,
      evidenceList: [],
      form: {},
      filters: {
        vessel_name: [],
        month_year: this.moment().format('MMM-YYYY'),
        sort_by: '',
        sort_dir: '',
        flf_fc_nominate: []
      },
      vesselOptions: [],
      tableHeaders: [
        { label: 'No', key: 'no', minWidth: '50px' },
        { label: 'Action', key: 'action', minWidth: '100px' },
        { label: 'Company', key: 'company', sortable: true },
        { label: 'Trip Number', key: 'trip_number', sortable: true },
        { label: 'Route', key: 'route', sortable: true },
        { label: 'Tug Name', key: 'tug', sortable: true },
        { label: 'Barge Name', key: 'barge', sortable: true },
        { label: 'Cargo Loaded', key: 'cargo_loaded', sortable: true },
        { label: 'Supply To Vessel', key: 'name_of_vessel', sortable: true }
      ],
      tableData: [],
      flfFcOptions: [],
      ongoingFields: [
        { label: 'Company', key: 'company' },
        { label: 'Trip Number', key: 'trip_number' },
        { label: 'Tug Name', key: 'tug' },
        { label: 'Barge Name', key: 'barge' },
        { label: 'Route', key: 'route' },
        { label: 'Cargo Loaded', key: 'cargo_loaded' },
        { label: 'Supply To Vessel', key: 'name_of_vessel' }
      ],
      reportedFields: [
        { label: 'Company', key: 'company' },
        { label: 'Trip Number', key: 'trip_number' },
        { label: 'Tug Name', key: 'tug' },
        { label: 'Barge Name', key: 'barge' },
        { label: 'Route', key: 'route' },
        { label: 'Cargo Loaded', key: 'cargo_loaded' },
        { label: 'Supply To Vessel', key: 'name_of_vessel' },
        { label: 'Date Of Incident', key: 'date_of_incident', isDate: true },
        { label: 'Location Of Incident', key: 'location_of_incident' },
        { label: 'Est. Lost by Visual Cargo', key: 'estimated_lost_cargo' },
        { label: 'Remark', key: 'remarks' },
        { label: 'Report Submitted Date', key: 'last_updated', isDate: true },
        { label: 'Reported By', key: 'last_updated_by' }
      ],
      activeTab: 'summary_cargo_stolen',
      tabs: [
        { label: 'Summary Cargo Stolen', value: 'summary_cargo_stolen' },
        { label: 'Ongoing Trips', value: 'ongoing_trips' },
        { label: 'Reported Accident', value: 'reported_accident' }
      ]
    }
  },
  watch: {
    cargoStolen: {
      handler(val) {
        this.tableData = val || []
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchCargoStolen(),
        this.fetchPercentageCargoStolenChart(),
        this.fetchSummaryCargoStolen()
      }
    },
    activeTab: {
      handler(val) {
        this.tableData = []
        this.fetchCargoStolen()
        this.fetchPercentageCargoStolenChart()
        this.fetchSummaryCargoStolen()
      }
    },
    vesselCargoStolen: {
      handler(val) {
        this.vesselOptions = val.map(item => ({
          text: item,
          value: item
        })) || []
      }
    },
    flfFcNominates: {
      handler(val) {
        if (val) {
          this.flfFcOptions = val
        }
      }
    },
    isDownload: {
      handler(val) {
        if (val) {
          this.exportCargoStolen()
        }
      }
    },
    cargoStolenFiles: {
      handler(val) {
        if (val && val.headers && val.data) {
          const formattedMonthYear = this.filters.month_year ? this.moment(this.filters.month_year, 'MMM-YYYY').format('MMM YYYY') : '';
          downloadUtils(val, `Report Cargo Stolen ${formattedMonthYear}.xlsx`)
          this.$store.dispatch('CargoStolen/resetFileExport')
          this.isDownload = false
        }
      }
    },
    me: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.filters.flf_fc_nominate = [{ value: val.flfFcNominate.id, text: val.flfFcNominate.name }]
        }
      }
    }
  },
  async mounted() {
    if (this.$route.query.month_year) {
      const monthYearParam = this.$route.query.month_year
      try {
        const parsedDate = moment(monthYearParam, 'MM-YYYY')
        if (parsedDate.isValid()) {
          this.filters.month_year = parsedDate.format('MMM-YYYY')
        }
      } catch (e) {
        this.showFailedNotification(this, 'Invalid month_year parameter format, please try again')
      }
    }
    this.fetchFlfFcNominates()
    this.fetchCargoStolen()
    this.fetchVesselCargoStolen()
    this.fetchPercentageCargoStolenChart()
    this.fetchSummaryCargoStolen()
  },
  methods: {
    async fetchFlfFcNominates() {
      await this.$store.dispatch('VesselLineUp/getFlfFcNominates')
    },
    async fetchCargoStolen() {
      this.isBusy = true
      try {
        const apiFilters = {
          ...(this.filters?.sort_dir ? {sort_dir: this.filters?.sort_dir.toUpperCase()}:null),
          ...(this.filters?.sort_by ? {sort_by: this.filters.sort_by} : null),
          page: this.currentPage || 1,
          limit: this.pageSize || 10,
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null,
          vessel_name: this.filters.vessel_name.map(item => item.value),
          flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value),
          is_list_incident: this.activeTab === 'reported_accident' ? true : false
        };

        await this.$store.dispatch('CargoStolen/fetchCargoStolen', apiFilters)
        this.isBusy = false
      } catch (err) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch cargo stolen list, please try again')
      }
    },
    async fetchVesselCargoStolen() {
      try {
        await this.$store.dispatch('CargoStolen/fetchVesselCargoStolen', this.filters)
      } catch (err) {
        this.showFailedNotification(this, 'Failed to fetch vessel cargo stolen list, please try again')
      }
    },
    async fetchPercentageCargoStolenChart() {
      try {
        this.isBusy = true
        await this.$store.dispatch('CargoStolen/fetchPercentageCargoStolenChart', {
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null,
          vessel_name: this.filters.vessel_name.map(item => item.value),
          flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value)
        })
        this.isBusy = false
      } catch (err) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch percentage cargo stolen chart, please try again')
      }
    },
    async fetchSummaryCargoStolen() {
      try {
        this.isBusy = true
        await this.$store.dispatch('CargoStolen/fetchSummaryCargoStolen', {
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null,
          vessel_name: this.filters.vessel_name.map(item => item.value),
          flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value)
        })
        this.isBusy = false
      } catch (err) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch summary cargo stolen, please try again')
      }
    },
    async exportCargoStolen() {
      try {
        this.isBusy = true
        await this.$store.dispatch('CargoStolen/exportCargoStolen', {
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null,
          vessel_name: this.filters.vessel_name.map(item => item.value),
          flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value)
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        })
        this.isDownload = false
        this.isBusy = false
      } catch (error) {
        this.isDownload = false
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download export, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchCargoStolen()
    },
    editCargoPopupShown(row) {
      this.isEditCargoPopupShown = true
      this.form = row
    },
    async viewAttachments(row) {
      this.isBusy = true
      await this.$store.dispatch('CargoStolen/fetchDetailCargoStolen', row.id)
      const detail = this.$store.getters['CargoStolen/detailCargoStolen']
      this.evidenceList = detail.evidences
      this.isBusy = false
      this.isEvidenceViewerShown = true
    },
    async handleUpdated() {
      this.isEditCargoPopupShown = false;
      await this.fetchCargoStolen();
    },
    async sendCargoStolenEmail() {
      this.isBusy = true
      try {
        await this.$store.dispatch('CargoStolen/postCargoStolenSendEmail', {
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null,
          ...(this.filters.vessel_name.length ? { vessel_name: this.filters.vessel_name.map(item => item.value.toString()) } : null),
          ...(this.filters.flf_fc_nominate.length ? { flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value.toString()) } : null)
        })

        this.showSuccessNotification(this, 'Cargo stolen email successfully sent')
      } catch ({ data }) {
        const message = data?.error?.message || 'Failed to send cargo stolen email, please try again'
        this.showFailedNotification(this, message)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filters = {
        vessel_name: [],
        month_year: this.moment().format('MMM-YYYY'),
        sort_by: '',
        sort_dir: '',
        flf_fc_nominate: []
      }
      if (this.isFlfFc) {
        this.filters.flf_fc_nominate = [{ value: this.me.flfFcNominate.id, text: this.me.flfFcNominate.name }]
      }
      this.$refs.tabletop.$emit('reset-search')
      this.fetchCargoStolen()
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.date-picker-wrapper,
.date-picker-wrapper * {
  width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
  max-width: 100% !important;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;
}
</style>
